import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { useSession } from "../../../context/SessionContext";
import {
  defaultTenant,
  getImageByFileName,
  updateProfile,
  uploadAvatar,
  uploadPlayerImage,
} from "../../../services/academicService";
import Swal from "sweetalert2";

const UserProfile = () => {
  const userSession = useSession();
  const isReadOnly = userSession?.role === "super-admin";
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getProfileData();
  }, []);

  const getAvatarFileName = (data) =>
    data?.avatar?.fileName ||
    data?.avatar ||
    data?.profileImage?.fileName ||
    "";

  const loadAvatar = async (fileName) => {
    if (!fileName) return;
    try {
      const res = await getImageByFileName(fileName);
      setAvatarUrl(res?.fileUrl || "");
    } catch (error) {
      console.error("Failed to load avatar:", error);
    }
  };

  const applyProfile = (data) => {
    if (!data) return;
    setProfileData(data);
    setForm({
      name: data?.name || "",
      phone: data?.phone || "",
      // address: data?.address || "",
    });
    loadAvatar(getAvatarFileName(data));
  };

  const getProfileData = async () => {
    const res = await defaultTenant();
    applyProfile(res);
  };

  const handleImageFileChange = async (e) => {
    if (isReadOnly) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // 1st API: upload the raw file
      let uploaded;

      try {
        uploaded = await uploadPlayerImage(file);
      } catch (error) {
        console.error("Upload error:", error);

        let message = "Image upload failed.";

        if (error?.response?.status === 413) {
          message = "Image size is too large. Please upload a smaller image.";
        } else if (error?.response?.data?.detail) {
          message = error.response.data.detail;
        } else if (error?.response?.data?.errorMessage) {
          message = error.response.data.errorMessage;
        } else if (error?.message) {
          message = error.message;
        }

        Swal.fire("Error!", message, "error");
        setUploading(false);
        return;
      }

      const payload = {
        avatar: uploaded,
      };

      // 2nd API: attach the uploaded file response to the user's avatar
      const res = await uploadAvatar(payload);

      if (res?.name || res?.email) {
        applyProfile(res);
      } else {
        setProfileData((prev) => ({ ...prev, avatar: uploaded }));
        loadAvatar(uploaded?.fileName);
      }

      window.dispatchEvent(new Event("avatar-updated"));
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        error?.response?.detail ||
        "Something went wrong";

      Swal.fire("Error!", message, "error");
      console.error("Image upload failed:", error);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (isReadOnly) return;

    // all fields are optional, only send the ones that have a value
    const payload = {};
    if (form.name?.trim()) payload.name = form.name.trim();
    if (form.phone?.toString().trim())
      payload.phone = form.phone.toString().trim();
    if (form.address?.trim()) payload.address = form.address.trim();

    setSaving(true);
    try {
      const res = await updateProfile(payload);
      if (res?.name || res?.email) {
        applyProfile(res);
      } else {
        setProfileData((prev) => ({ ...prev, ...payload }));
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Profile update failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: profileData?.name || "",
      phone: profileData?.phone || "",
      address: profileData?.address || "",
    });
    setIsEditing(false);
  };

  return (
    <>
      <PageTitle activeMenu="Profile" motherMenu={"User Profile"} />
      <div className="row">
        <div className="col-xl-3 col-lg-4">
          <div className="clearfix">
            <div className="card card-bx profile-card author-profile mb-3">
              <div className="card-body">
                <div className="p-5">
                  <div className="author-profile">
                    <div className="author-media">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt=""
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center bg-light text-muted"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                          }}
                        >
                          <i
                            className="fa fa-user"
                            style={{ fontSize: "48px" }}
                          />
                        </div>
                      )}
                      <div
                        className="upload-link"
                        title="Update"
                        data-toggle="tooltip"
                        data-placement="right"
                        data-original-title="update"
                        style={{ display: isReadOnly ? "none" : undefined }}
                      >
                        <input
                          type="file"
                          className="update-flie"
                          accept="image/*"
                          onChange={handleImageFileChange}
                          disabled={uploading || isReadOnly}
                        />
                        <i
                          className={
                            uploading ? "fa fa-spinner fa-spin" : "fa fa-camera"
                          }
                        ></i>
                      </div>
                    </div>
                    <div className="author-info">
                      <h6 className="title d-flex align-items-center justify-content-center gap-2">
                        {profileData?.name}
                        {!isEditing && !isReadOnly && (
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            title="Edit profile"
                            onClick={() => setIsEditing(true)}
                          >
                            <i className="fa fa-pencil" />
                          </button>
                        )}
                      </h6>
                      <span>
                        {profileData?.defaultTenant?.admin
                          ? "Admin"
                          : profileData?.defaultTenant?.userType}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-9 col-lg-8">
          <div className="card profile-card card-bx ">
            <div className="card-header">
              <h6 className="card-title">Account setup</h6>
            </div>
            <form
              className="profile-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={form.name}
                      onChange={handleFieldChange}
                      readOnly={!isEditing}
                    />
                  </div>
                  {/* <div className="col-sm-6 mb-3">
                    <label className="form-label">Gender</label>
                    <Select
                      options={options1}
                      isSearchable={false}
                      defaultValue={options1[1]}
                      className="custom-react-select"
                    />
                  </div> */}
                  {/* <div className="col-sm-6 mb-3">
                    <label className="form-label">Birth</label>
                    <div className="input-hasicon mb-xl-0 mb-3">
                      <DatePicker
                        defaultValue={new Date("01/06/2024")}
                        className="picker-suit"
                      />
                      <div className="icon">
                        <i className="far fa-calendar" />
                      </div>
                    </div>
                  </div> */}
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      className="form-control"
                      value={form.phone}
                      onChange={handleFieldChange}
                      readOnly={!isEditing}
                    />
                  </div>
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Email address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={profileData?.email || ""}
                      readOnly
                    />
                  </div>
                  {/* <div className="col-sm-6 mb-3">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      name="address"
                      className="form-control"
                      value={form.address}
                      onChange={handleFieldChange}
                      readOnly={!isEditing}
                    />
                  </div> */}
                  {/* <div className="col-sm-6 mb-3">
                    <label className="form-label">Country</label>
                    <Select
                      options={options2}
                      isSearchable={false}
                      defaultValue={options2[1]}
                      className="custom-react-select"
                    />
                  </div> */}
                  {/* <div className="col-sm-6 mb-3">
                    <label className="form-label">City</label>
                    <Select
                      options={options3}
                      isSearchable={false}
                      defaultValue={options3[1]}
                      className="custom-react-select"
                    />
                  </div> */}
                </div>
              </div>
              <div className="card-footer align-items-center d-flex">
                {isEditing && (
                  <>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "SAVING..." : "SAVE"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light ms-2"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      CANCEL
                    </button>
                  </>
                )}
                {/* <Link to={"#"} className="btn-link float-end ms-auto">
                  Forgot your password?
                </Link> */}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default UserProfile;
